import React, { useEffect, useState, useContext } from 'react';
import { AddorEditSubCompany, EditShops } from './Form';
import { getSubCompany, deleteSubCompany, getShopsByCom, deleteShops, BASE_URL } from '../../../Utility/Api';
import { WarehouseContext } from '../../../Utility/UserContext';
import { toast } from 'react-toastify';
import { Deletemodel } from '../../Common/Functions';
import { setLocalStorage, NaviGate } from '../../../Utility/function';
import { Pencil, Trash2, Plus, RotateCw, Search, Building2, GitBranch, Store } from 'lucide-react';

const SubCompany = () => {
  const { shopItem, setShopItem, subCompany, setSubCompany } = useContext(WarehouseContext);

  // Sub Companies State
  const [subCompanies, setSubCompanies] = useState([]);
  const [isLoadingSubCompanies, setIsLoadingSubCompanies] = useState(false);
  const [searchSubCompany, setSearchSubCompany] = useState("");
  const [selectedSubCompany, setSelectedSubCompany] = useState(null);
  const [showSubCompanyModal, setShowSubCompanyModal] = useState(false);
  const [selectedSubCompanyForEdit, setSelectedSubCompanyForEdit] = useState(null);

  // Branches State
  const [branches, setBranches] = useState([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [searchBranch, setSearchBranch] = useState("");
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [selectedBranchForEdit, setSelectedBranchForEdit] = useState(null);

  // Deletion State
  const [deletemodel, showDeletemodel] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: "", type: "subcompany", title: "" });

  useEffect(() => {
    initLoad();
  }, []);

  const initLoad = async () => {
    setIsLoadingSubCompanies(true);
    try {
      const response = await getSubCompany(1, "");
      if (response.data && response.data.data) {
        const list = response.data.data;
        setSubCompanies(list);

        let initialSelect = null;
        if (subCompany?.id && list.some(item => String(item.id) === String(subCompany.id))) {
          initialSelect = list.find(item => String(item.id) === String(subCompany.id));
        } else if (list.length > 0) {
          initialSelect = list[0];
        }

        if (initialSelect) {
          setSelectedSubCompany(initialSelect);
          fetchBranches(initialSelect.id);
        }
      } else {
        setSubCompanies([]);
        setBranches([]);
      }
    } catch (error) {
      console.error('Error loading sub companies:', error);
      toast.error("Failed to load sub companies");
    } finally {
      setIsLoadingSubCompanies(false);
    }
  };

  const fetchSubCompanies = async (text = "") => {
    setIsLoadingSubCompanies(true);
    try {
      const response = await getSubCompany(1, text);
      if (response.data && response.data.data) {
        const list = response.data.data;
        setSubCompanies(list);

        // Keep current selected if still in list, otherwise select first
        setSelectedSubCompany(prev => {
          if (prev && list.some(item => String(item.id) === String(prev.id))) {
            const updated = list.find(item => String(item.id) === String(prev.id));
            fetchBranches(updated.id);
            return updated;
          }
          if (list.length > 0) {
            fetchBranches(list[0].id);
            return list[0];
          }
          setBranches([]);
          return null;
        });
      } else {
        setSubCompanies([]);
        setBranches([]);
      }
    } catch (error) {
      console.error('Error fetching sub companies:', error);
    } finally {
      setIsLoadingSubCompanies(false);
    }
  };

  const fetchBranches = async (subCompanyId) => {
    if (!subCompanyId) {
      setBranches([]);
      return;
    }
    setIsLoadingBranches(true);
    try {
      const response = await getShopsByCom(subCompanyId);
      if (response?.data?.data) {
        const data = response.data.data.map((item) => ({
          ...item,
          shop_id: item.shop_id || item.id,
          shop_title: item.shop_title || item.title,
        }));
        setBranches(data);
      } else {
        setBranches([]);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    } finally {
      setIsLoadingBranches(false);
    }
  };

  const handleSelectSubCompany = (comp) => {
    setSelectedSubCompany(comp);
    fetchBranches(comp.id);
  };

  // Sub Company Modal Handlers
  const openAddSubCompanyModal = () => {
    setSelectedSubCompanyForEdit(null);
    setShowSubCompanyModal(true);
  };

  const openEditSubCompanyModal = (comp, e) => {
    if (e) e.stopPropagation();
    setSelectedSubCompanyForEdit({
      id: comp.id,
      title: comp.title,
      Address: comp.Address,
      phone1: comp.phone1,
      phone2: comp.phone2,
      email: comp.email,
      ...comp
    });
    setShowSubCompanyModal(true);
  };

  // Branch Modal Handlers
  const openAddBranchModal = () => {
    setSelectedBranchForEdit(null);
    setShowBranchModal(true);
  };

  const openEditBranchModal = (branch, e) => {
    if (e) e.stopPropagation();
    setSelectedBranchForEdit({
      id: branch.shop_id || branch.id,
      title: branch.shop_title || branch.title,
      Address: branch.Address,
      phone1: branch.phone1,
      phone2: branch.phone2,
      email: branch.email,
      branch_id: branch.branch_id,
      scomp_id: branch.scomp_id || selectedSubCompany?.id,
      ...branch
    });
    setShowBranchModal(true);
  };

  // Delete Handlers
  const handleDeleteClick = (id, type, title, e) => {
    if (e) e.stopPropagation();
    setDeleteTarget({ id, type, title });
    showDeletemodel(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget.id) return;
    try {
      if (deleteTarget.type === "subcompany") {
        await deleteSubCompany(deleteTarget.id);
        toast.success("Shop deleted successfully");
        showDeletemodel(false);
        if (subCompany && String(subCompany.id) === String(deleteTarget.id)) {
          setLocalStorage("SUB-COMPANY", "null");
          setSubCompany(null);
          setLocalStorage("SHOP", "null");
          setShopItem(null);
          window.location.reload();
        } else {
          await fetchSubCompanies();
        }
      } else {
        await deleteShops(deleteTarget.id);
        toast.success("Branch deleted successfully");
        showDeletemodel(false);
        const currentShopId = shopItem?.shop_id || shopItem?.id;
        if (currentShopId && String(currentShopId) === String(deleteTarget.id)) {
          setLocalStorage("SHOP", "null");
          setShopItem(null);
          window.location.reload();
        } else {
          if (selectedSubCompany?.id) {
            await fetchBranches(selectedSubCompany.id);
          }
        }
      }
    } catch (err) {
      console.error("Error deleting item:", err);
      toast.error("Failed to delete item");
    }
  };

  // Filter lists based on search
  const filteredSubCompanies = subCompanies.filter(comp =>
    (comp.title || "").toLowerCase().includes(searchSubCompany.toLowerCase())
  );

  const filteredBranches = branches.filter(branch =>
    (branch.shop_title || branch.title || "").toLowerCase().includes(searchBranch.toLowerCase())
  );

  return (
    <div className="page-content py-3 px-3">
      <NaviGate />

      {/* Main Title Header */}
      <div className="mb-4">
        <h4 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
          <Building2 className="text-primary" size={24} />
          Shops & Branch Management
        </h4>
        <p className="text-muted small mb-0">
          Select a shops on the left to view and manage its corresponding branches on the right.
        </p>
      </div>

      {/* Side-by-Side Dual Table Container */}
      <div className="row g-4">
        {/* ============================================================== */}
        {/* LEFT COLUMN: SUB COMPANY LIST */}
        {/* ============================================================== */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm border-0 h-100" style={{ borderRadius: "12px", minHeight: "650px", backgroundColor: "#fff" }}>
            {/* Card Header */}
            <div className="card-header bg-white border-0 pt-3 pb-2 px-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-bold fs-6 text-uppercase" style={{ color: "#1e293b", letterSpacing: "0.5px" }}>
                    Shops List
                  </span>
                  {/* <span className="badge bg-light text-secondary border px-2 py-1">
                    {filteredSubCompanies.length}
                  </span> */}
                </div>

                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-light border rounded-circle d-inline-flex align-items-center justify-content-center"
                    style={{ width: "36px", height: "36px" }}
                    onClick={() => fetchSubCompanies(searchSubCompany)}
                    title="Refresh Sub Companies"
                  >
                    <RotateCw size={16} className={isLoadingSubCompanies ? "text-primary spin-animation" : "text-secondary"} />
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary btn-sm px-3 py-2 fw-semibold d-inline-flex align-items-center gap-1 rounded shadow-sm"
                    onClick={openAddSubCompanyModal}
                    style={{ backgroundColor: "#3b82f6", borderColor: "#3b82f6" }}
                  >
                    <Plus size={16} />
                    Add Shop
                  </button>
                </div>
              </div>

              {/* Search Bar */}
              <div className="input-group input-group-sm mt-1">
                <span className="input-group-text bg-light border-end-0 text-muted">
                  <Search size={14} />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-1 bg-light"
                  placeholder="Search shop by name..."
                  value={searchSubCompany}
                  onChange={(e) => setSearchSubCompany(e.target.value)}
                />
                {searchSubCompany && (
                  <button
                    className="btn btn-sm btn-light border border-start-0 text-muted"
                    type="button"
                    onClick={() => setSearchSubCompany("")}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* Table Body */}
            <div className="card-body p-0">
              <div className="table-responsive px-3 pb-3">
                <table className="table table-hover align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: "0 4px" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#e8f0fe" }}>
                      <th className="text-center py-2 px-3 fw-bold text-secondary" style={{ width: "12%", fontSize: "13px", borderTopLeftRadius: "6px", borderBottomLeftRadius: "6px" }}>
                        S.no
                      </th>
                      <th className="text-start py-2 px-3 fw-bold text-secondary" style={{ width: "63%", fontSize: "13px" }}>
                        Shop
                      </th>
                      <th className="text-center py-2 px-3 fw-bold text-secondary" style={{ width: "25%", fontSize: "13px", borderTopRightRadius: "6px", borderBottomRightRadius: "6px" }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingSubCompanies && subCompanies.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="text-center py-5 text-muted">
                          <span className="spinner-border spinner-border-sm me-2 text-primary" role="status" aria-hidden="true"></span>
                          Loading shops...
                        </td>
                      </tr>
                    ) : filteredSubCompanies.length > 0 ? (
                      filteredSubCompanies.map((comp, index) => {
                        const isSelected = selectedSubCompany?.id === comp.id;
                        return (
                          <tr
                            key={comp.id}
                            onClick={() => handleSelectSubCompany(comp)}
                            style={{
                              cursor: "pointer",
                              backgroundColor: isSelected ? "#e0edff" : "transparent",
                              transition: "background-color 0.15s ease",
                              borderLeft: isSelected ? "4px solid #3b82f6" : "4px solid transparent"
                            }}
                          >
                            <td className="text-center py-2 px-3 text-secondary fw-semibold" style={{ fontSize: "13px" }}>
                              {index + 1}
                            </td>
                            <td className="text-start py-2 px-3">
                              <div className="d-flex align-items-center gap-2">
                                <img
                                  src={comp.logo ? BASE_URL + comp.logo : '/assets/images/tech_logo.webp'}
                                  alt=""
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    objectFit: "contain",
                                    borderRadius: "6px",
                                    border: "1px solid #e2e8f0",
                                    backgroundColor: "#fff",
                                    padding: "2px"
                                  }}
                                  onError={(e) => { e.target.src = '/assets/images/tech_logo.webp'; }}
                                />
                                <span className={isSelected ? "fw-bold text-primary" : "text-dark fw-medium"}>
                                  {comp.title}
                                </span>
                              </div>
                            </td>
                            <td className="text-center py-2 px-3">
                              <div className="d-flex justify-content-center align-items-center gap-2">
                                <button
                                  type="button"
                                  className="btn btn-sm rounded-circle d-inline-flex align-items-center justify-content-center shadow-none"
                                  style={{ width: "32px", height: "32px", backgroundColor: "#e0f2fe", color: "#0284c7", border: "none" }}
                                  onClick={(e) => openEditSubCompanyModal(comp, e)}
                                  title="Edit Shop"
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm rounded-circle d-inline-flex align-items-center justify-content-center shadow-none"
                                  style={{ width: "32px", height: "32px", backgroundColor: "#fee2e2", color: "#ef4444", border: "none" }}
                                  onClick={(e) => handleDeleteClick(comp.id, "subcompany", comp.title, e)}
                                  title="Delete Shop"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center py-5 text-muted">
                          <Store size={36} className="text-secondary opacity-50 mb-2 d-block mx-auto" />
                          No shops found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* RIGHT COLUMN: BRANCHES / SHOPS OF SELECTED SUB COMPANY */}
        {/* ============================================================== */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm border-0 h-100" style={{ borderRadius: "12px", minHeight: "650px", backgroundColor: "#fff" }}>
            {/* Card Header */}
            <div className="card-header bg-white border-0 pt-3 pb-2 px-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                  <span className="fw-bold fs-6 text-uppercase text-truncate" style={{ color: "#1e293b", letterSpacing: "0.5px" }}>
                    Branches of {selectedSubCompany?.title || "..."}
                  </span>
                  {/* <span className="badge bg-light text-secondary border px-2 py-1">
                    {filteredBranches.length}
                  </span> */}
                </div>

                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-light border rounded-circle d-inline-flex align-items-center justify-content-center"
                    style={{ width: "36px", height: "36px" }}
                    onClick={() => {
                      if (selectedSubCompany?.id) {
                        fetchBranches(selectedSubCompany.id);
                      }
                    }}
                    title="Refresh Branches"
                    disabled={!selectedSubCompany}
                  >
                    <RotateCw size={16} className={isLoadingBranches ? "text-primary spin-animation" : "text-secondary"} />
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary btn-sm px-3 py-2 fw-semibold d-inline-flex align-items-center gap-1 rounded shadow-sm text-nowrap"
                    onClick={openAddBranchModal}
                    disabled={!selectedSubCompany}
                    style={{ backgroundColor: "#3b82f6", borderColor: "#3b82f6" }}
                  >
                    <Plus size={16} />
                    Add Branch
                  </button>
                </div>
              </div>

              {/* Search Bar */}
              <div className="input-group input-group-sm mt-1">
                <span className="input-group-text bg-light border-end-0 text-muted">
                  <Search size={14} />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-1 bg-light"
                  placeholder="Search branch by name..."
                  value={searchBranch}
                  onChange={(e) => setSearchBranch(e.target.value)}
                  disabled={!selectedSubCompany}
                />
                {searchBranch && (
                  <button
                    className="btn btn-sm btn-light border border-start-0 text-muted"
                    type="button"
                    onClick={() => setSearchBranch("")}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* Table Body */}
            <div className="card-body p-0">
              <div className="table-responsive px-3 pb-3">
                <table className="table table-hover align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: "0 4px" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#e8f0fe" }}>
                      <th className="text-center py-2 px-3 fw-bold text-secondary" style={{ width: "12%", fontSize: "13px", borderTopLeftRadius: "6px", borderBottomLeftRadius: "6px" }}>
                        S.no
                      </th>
                      <th className="text-start py-2 px-3 fw-bold text-secondary" style={{ width: "63%", fontSize: "13px" }}>
                        Branch
                      </th>
                      <th className="text-center py-2 px-3 fw-bold text-secondary" style={{ width: "25%", fontSize: "13px", borderTopRightRadius: "6px", borderBottomRightRadius: "6px" }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {!selectedSubCompany ? (
                      <tr>
                        <td colSpan="3" className="text-center py-5 text-muted">
                          <Store size={40} className="text-secondary opacity-50 mb-2 d-block mx-auto" />
                          <p className="mb-0">Please select a shop from the left list.</p>
                        </td>
                      </tr>
                    ) : isLoadingBranches ? (
                      <tr>
                        <td colSpan="3" className="text-center py-5 text-muted">
                          <span className="spinner-border spinner-border-sm me-2 text-primary" role="status" aria-hidden="true"></span>
                          Loading branches...
                        </td>
                      </tr>
                    ) : filteredBranches.length > 0 ? (
                      filteredBranches.map((branch, index) => (
                        <tr
                          key={branch.shop_id || branch.id}
                          style={{ transition: "background-color 0.15s ease" }}
                        >
                          <td className="text-center py-2 px-3 text-secondary fw-semibold" style={{ fontSize: "13px" }}>
                            {index + 1}
                          </td>
                          <td className="text-start py-2 px-3">
                            <div>
                              <div className="fw-semibold text-dark" style={{ fontSize: "14px" }}>
                                {branch.shop_title || branch.title}
                              </div>
                              {branch.Address && (
                                <small className="text-muted text-truncate d-block" style={{ fontSize: "11px", maxWidth: "250px" }}>
                                  {branch.Address}
                                </small>
                              )}
                            </div>
                          </td>
                          <td className="text-center py-2 px-3">
                            <div className="d-flex justify-content-center align-items-center gap-2">
                              <button
                                type="button"
                                className="btn btn-sm rounded-circle d-inline-flex align-items-center justify-content-center shadow-none"
                                style={{ width: "32px", height: "32px", backgroundColor: "#e0f2fe", color: "#0284c7", border: "none" }}
                                onClick={(e) => openEditBranchModal(branch, e)}
                                title="Edit Branch"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm rounded-circle d-inline-flex align-items-center justify-content-center shadow-none"
                                style={{ width: "32px", height: "32px", backgroundColor: "#fee2e2", color: "#ef4444", border: "none" }}
                                onClick={(e) => handleDeleteClick(branch.shop_id || branch.id, "branch", branch.shop_title || branch.title, e)}
                                title="Delete Branch"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center py-5 text-muted">
                          <GitBranch size={40} className="text-secondary opacity-50 mb-2 d-block mx-auto" />
                          <p className="fw-medium mb-1">No branches registered under {selectedSubCompany?.title}.</p>
                          <p className="small text-secondary mb-3">Click '+ Add Branch' to create the first branch.</p>
                          {/* <button
                            type="button"
                            className="btn btn-sm btn-outline-primary px-3"
                            onClick={openAddBranchModal}
                          >
                            <Plus size={14} className="me-1" />
                            Add Branch
                          </button> */}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deletemodel && (
        <Deletemodel
          setShowModal={() => showDeletemodel(false)}
          handleDelete={handleDeleteConfirm}
        />
      )}

      {/* Sub Company Add / Edit Modal */}
      <AddorEditSubCompany
        showModal={showSubCompanyModal}
        setShowModal={() => setShowSubCompanyModal(false)}
        fetchdata={() => fetchSubCompanies()}
        selectedShops={selectedSubCompanyForEdit}
        AddorEdit={!selectedSubCompanyForEdit}
      />

      {/* Branch / Shop Add / Edit Modal */}
      <EditShops
        showModal={showBranchModal}
        setShowModal={() => setShowBranchModal(false)}
        fetchdata={() => {
          if (selectedSubCompany?.id) {
            fetchBranches(selectedSubCompany.id);
          }
        }}
        selectedShops={selectedBranchForEdit}
        defaultScompId={selectedSubCompany?.id}
        AddorEdit={!selectedBranchForEdit}
      />

      {/* Keyframe animation for spinner */}
      <style>{`
        .spin-animation {
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SubCompany;
